import { ThemeEvents, CartAddEvent } from '@theme/events';
import {
  trackAddToCart,
  trackRemoveFromCart,
  trackViewCart,
  resolveListContext,
  setGtmCartAttributes,
  normalizeShopifyResourceId,
} from '@theme/gtm-events';

// ---------------------------------------------------------------------------
// Capture-phase click listener: runs before product-form-component's submit handler.
//
// 1. Detects purchase type (subscription vs one-time) from the Recharge selling_plan
//    input and writes it to data-purchase-type / data-delivery-frequency on the
//    form component so the CartAddEvent handler can read them.
//
// 2. Injects _item_list_id / _item_list_name as hidden form inputs so they travel
//    as line item properties to checkout where the Custom Pixel can read them.
//    (product-form.js uses new FormData(form) which picks up all hidden inputs.)
// ---------------------------------------------------------------------------
document.addEventListener('click', (e) => {
  const btn = /** @type {Element} */ (e.target)?.closest?.('[type="submit"]');
  if (!btn) return;

  const formComponent = btn.closest('product-form-component');
  if (!formComponent) return;

  const form = formComponent.querySelector('form');
  if (!form) return;

  // --- 1. Purchase type detection ---
  // Recharge may inject selling_plan as input or select; also check outside <form>
  // (widget sometimes mounts as a sibling within product-form-component).
  const sellingPlanEl = /** @type {HTMLInputElement|HTMLSelectElement|null} */ (
    form.querySelector('input[name="selling_plan"], select[name="selling_plan"]') ??
    formComponent.querySelector('input[name="selling_plan"], select[name="selling_plan"]')
  );
  const sellingPlan = sellingPlanEl?.value ?? '';
  const isSubscription = sellingPlan !== '';

  let deliveryFrequency = '';
  if (isSubscription) {
    // Best-effort: read frequency label from Recharge widget shadow DOM.
    // The widget marks the selected option with [rc-selected]; the frequency
    // is rendered in a child with part "rc-purchase-option__frequency".
    const section = formComponent.closest('.shopify-section') ?? document;
    const widget = section.querySelector('recharge-subscription-widget');
    const selectedOption = widget?.shadowRoot?.querySelector('[rc-selected]');
    const freqEl = selectedOption?.querySelector('[part~="rc-purchase-option__frequency"]');
    deliveryFrequency = freqEl?.textContent?.trim() ?? '';

    // Fallback: selling plan name from a native select option
    if (!deliveryFrequency && sellingPlanEl instanceof HTMLSelectElement) {
      deliveryFrequency = sellingPlanEl.selectedOptions?.[0]?.textContent?.trim() ?? '';
    }
  }

  const purchaseType = isSubscription ? 'subscription' : 'one_time';
  /** @type {HTMLElement} */ (formComponent).dataset.purchaseType = purchaseType;
  /** @type {HTMLElement} */ (formComponent).dataset.deliveryFrequency = deliveryFrequency;

  // --- 2. Line item property injection ---
  // Remove any previously injected inputs to avoid duplicates
  form.querySelectorAll('[data-gtm-list-prop]').forEach((el) => el.remove());

  // Always write purchase metadata so view_cart / remove_from_cart can read them back
  const propsToInject = /** @type {[string, string][]} */ ([
    ['_purchase_type',      purchaseType],
    ['_delivery_frequency', deliveryFrequency],
  ]);

  // Box metadata from product data (available on the form component)
  let productData = null;
  try {
    productData = JSON.parse(/** @type {HTMLElement} */ (formComponent).dataset.gtmProduct ?? '');
  } catch (_) {}

  if (productData?.box_type  != null) propsToInject.push(['_box_type',     String(productData.box_type)]);
  if (productData?.box_size  != null) propsToInject.push(['_box_size',     String(productData.box_size)]);
  if (productData?.box_capacity != null) propsToInject.push(['_box_capacity', String(productData.box_capacity)]);

  // List context (browse attribution, or empty when none)
  const productId = /** @type {HTMLElement} */ (formComponent).dataset.productId;
  if (productId) {
    const ctx = resolveListContext(productId);
    if (ctx.item_list_id) {
      propsToInject.push(['_item_list_id', ctx.item_list_id]);
      propsToInject.push(['_item_list_name', ctx.item_list_name ?? '']);
    }
    if (ctx.index != null) propsToInject.push(['_index', String(ctx.index)]);
    const gtmItemId = normalizeShopifyResourceId(productId);
    if (gtmItemId) propsToInject.push(['_gtm_item_id', gtmItemId]);
  }

  for (const [key, val] of propsToInject) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${key}]`;
    input.value = val;
    input.dataset.gtmListProp = '1';
    form.appendChild(input);
  }

  // Snapshot cart qty before add so capped (didError) adds can report the delta,
  // not the requested quantity from the form.
  let priorQty = 0;
  try {
    const cartComponent = document.querySelector('cart-items-component');
    const cart = JSON.parse(/** @type {HTMLElement|null} */ (cartComponent)?.dataset.cart ?? '{}');
    const variantId = /** @type {HTMLInputElement|null} */ (form.querySelector('input[name="id"]'))?.value;
    priorQty = (cart.items ?? [])
      .filter((item) =>
        variantId
          ? String(item.variant_id) === String(variantId)
          : String(item.product_id) === String(productId)
      )
      .reduce((sum, item) => sum + (item.quantity ?? 0), 0);
  } catch (_) {}
  /** @type {HTMLElement} */ (formComponent).dataset.gtmPriorQty = String(priorQty);
}, true);

/**
 * Fires add_to_cart after a successful CartAddEvent from the standard product form
 * or the custom-bundle PDP.
 *
 * Reads product data from data-gtm-product on <product-form-component>.
 * Reads quantity from the CartAddEvent detail (requested qty).
 * On max-qty / inventory-capped adds (didError), uses cart delta vs
 * data-gtm-prior-qty (snapshotted on click) so value/quantity match what Shopify added.
 *
 * BYB box adds are handled in gtm-byb-events.js (byb_product_added + rolled-up ATC).
 */
document.addEventListener(CartAddEvent.eventName, async (e) => {
  const event = /** @type {CartAddEvent} */ (e);
  const data = event.detail?.data ?? {};

  if (data.source === 'product-custom-bundle-add-to-cart') {
    await trackCustomBundleAddToCart(event);
    return;
  }

  if (data.source !== 'product-form-component') return;

  const form = /** @type {Element|null} */ (event.target)?.closest?.('[data-gtm-product]')
    ?? document.querySelector(`[data-product-id="${data.productId}"][data-gtm-product]`);

  if (!form) return;

  let productData;
  try {
    productData = JSON.parse(/** @type {HTMLElement} */ (form).dataset.gtmProduct ?? '');
  } catch (_) {
    return;
  }

  // Override price with the currently selected variant's base price to avoid stale
  // data after the user changes variant before clicking ATC. Subscription /
  // selling-plan discounts are applied below from the live cart line.
  const currentVariantId = /** @type {HTMLInputElement|null} */ (form.querySelector('input[name="id"]'))?.value ?? '';
  if (currentVariantId) {
    try {
      const variantsMap = JSON.parse(/** @type {HTMLElement} */ (form).dataset.gtmVariants ?? '{}');
      if (variantsMap[currentVariantId] != null) {
        productData.price = variantsMap[currentVariantId];
      }
    } catch (_) {}
  }

  let quantity = data.itemCount ?? 1;
  const formEl = /** @type {HTMLElement} */ (form);

  // Re-read selling_plan at event time (Recharge may update the input after click).
  // Fall back to cart line selling_plan_allocation when dataset was never set.
  const sellingPlanAtEvent =
    /** @type {HTMLInputElement|null} */ (form.querySelector('input[name="selling_plan"], select[name="selling_plan"]'))
      ?.value ?? '';
  let purchaseType =
    formEl.dataset.purchaseType ||
    (sellingPlanAtEvent ? 'subscription' : '') ||
    'one_time';
  let deliveryFrequency = formEl.dataset.deliveryFrequency ?? '';

  // Fetch live cart for subtotal — also used to verify partial adds on didError
  // and to read the actual line price (includes Recharge / selling_plan discounts).
  let cartSubtotal = 0;
  try {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    cartSubtotal = cart.items_subtotal_price ?? cart.total_price ?? 0;

    const matchingItems = (cart.items ?? []).filter((item) =>
      currentVariantId
        ? String(item.variant_id) === String(currentVariantId)
        : String(item.product_id) === String(data.productId)
    );

    if (data.didError) {
      if (matchingItems.length === 0) return;

      const cartQty = matchingItems.reduce((sum, item) => sum + (item.quantity ?? 0), 0);
      const priorQty = Number(formEl.dataset.gtmPriorQty ?? 0);
      quantity = Math.max(0, cartQty - priorQty);
      if (quantity === 0) return;
    }

    // Prefer the line that matches purchase type so subscription price ≠ one-time
    const fallbackLine = matchingItems[matchingItems.length - 1];
    const line =
      purchaseType === 'subscription' || sellingPlanAtEvent
        ? matchingItems.find((item) => item.selling_plan_allocation) ?? fallbackLine
        : matchingItems.find((item) => !item.selling_plan_allocation) ?? fallbackLine;

    if (line?.selling_plan_allocation) {
      purchaseType = 'subscription';
      if (!deliveryFrequency) {
        deliveryFrequency = line.selling_plan_allocation.selling_plan?.name ?? '';
      }
    } else if (!formEl.dataset.purchaseType && !sellingPlanAtEvent) {
      purchaseType = 'one_time';
    }

    if (line != null && (line.final_price != null || line.price != null)) {
      productData.price = line.final_price ?? line.price;
    }

    // Prefer box metadata from line properties when click-time injection ran
    if (line?.properties) {
      if (line.properties._box_type != null) productData.box_type = line.properties._box_type;
      if (line.properties._box_size != null) productData.box_size = Number(line.properties._box_size);
      if (line.properties._box_capacity != null) productData.box_capacity = Number(line.properties._box_capacity);
    }
  } catch (_) {
    if (data.didError) return;
  }

  const listCtx = resolveListContext(String(productData.item_id));

  trackAddToCart(
    {
      item_id: productData.item_id,
      item_name: productData.item_name,
      item_category: productData.item_category,
      price: productData.price,
      quantity,
      index: listCtx.index ?? 1,
      box_type: productData.box_type ?? null,
      box_size: productData.box_size != null ? Number(productData.box_size) : null,
      box_capacity: productData.box_capacity != null ? Number(productData.box_capacity) : null,
      purchase_type: purchaseType,
      delivery_frequency: deliveryFrequency,
      item_list_id: listCtx.item_list_id,
      item_list_name: listCtx.item_list_name,
    },
    cartSubtotal
  );

  // Persist tracking data as cart attributes for checkout Custom Pixel.
  setGtmCartAttributes(productData.item_id, {
    box_type: productData.box_type,
    box_size: productData.box_size != null ? Number(productData.box_size) : null,
    box_capacity: productData.box_capacity != null ? Number(productData.box_capacity) : null,
    item_list_id: listCtx.item_list_id,
    item_list_name: listCtx.item_list_name,
    index: listCtx.index,
  });
});

/**
 * Rolled-up add_to_cart for product-custom-bundle-component (variety/shell packs).
 * @param {CartAddEvent} event
 */
async function trackCustomBundleAddToCart(event) {
  const el =
    /** @type {HTMLElement|null} */ (event.target)?.closest?.('product-custom-bundle-component') ??
    document.querySelector('product-custom-bundle-component');
  if (!(el instanceof HTMLElement)) return;

  let packs = [];
  try {
    packs = JSON.parse(el.dataset.variantPacks ?? '[]');
  } catch (_) {}

  const checkedRadio = /** @type {HTMLInputElement|null} */ (
    el.querySelector('input[type="radio"][data-variant]:checked')
  );
  const selectedVariantId = checkedRadio?.dataset.variant ?? null;
  const variantPack =
    packs.find((p) => String(p.id) === String(selectedVariantId)) ?? packs[0] ?? null;

  const purchaseTypeInput = /** @type {HTMLInputElement|null} */ (
    el.querySelector('input[type="radio"][name*="purchase-type"]:checked')
  );
  const purchaseValue = purchaseTypeInput?.value ?? '';
  const isSubscribe = purchaseValue === 'subscribe' || purchaseValue === 'subscription';
  const purchaseType = isSubscribe ? 'subscription' : 'one_time';

  const sellingPlanSelect = el.querySelector('select');
  const deliveryFrequency =
    isSubscribe && sellingPlanSelect instanceof HTMLSelectElement
      ? sellingPlanSelect.selectedOptions?.[0]?.textContent?.trim() ?? ''
      : '';

  const packItems = isSubscribe ? variantPack?.subscription_packs : variantPack?.one_time_packs;
  const boxSize = Array.isArray(packItems)
    ? packItems.reduce((sum, pack) => sum + (Number(pack.quantity) || 0), 0)
    : null;
  const boxCapacity =
    el.dataset.gtmBoxCapacity != null && el.dataset.gtmBoxCapacity !== ''
      ? Number(el.dataset.gtmBoxCapacity)
      : boxSize;
  const boxType = el.dataset.gtmBoxType || 'Variety Pack';

  const unitPrice = Number(variantPack?.price ?? 0);
  let price = unitPrice;
  let cartSubtotal = unitPrice;

  const resource = event.detail?.resource;
  if (resource && Array.isArray(resource.items) && resource.items.length > 0) {
    const sum = resource.items.reduce(
      (s, item) => s + (Number(item.final_line_price ?? item.line_price) || 0),
      0
    );
    if (sum > 0) price = sum;
  }

  try {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    cartSubtotal = cart.items_subtotal_price ?? cart.total_price ?? unitPrice;
    if (!resource?.items?.length) {
      const productId = el.dataset.productId;
      const line = (cart.items ?? []).find(
        (item) =>
          String(item.product_id) === String(productId) ||
          String(item.variant_id) === String(selectedVariantId ?? variantPack?.id)
      );
      if (line != null && (line.final_price != null || line.price != null)) {
        price = line.final_line_price ?? line.final_price ?? line.price;
      }
    }
  } catch (_) {}

  const pid = el.dataset.productId || '';
  const listCtx = resolveListContext(pid, {
    item_list_id: el.dataset.gtmListId || '',
    item_list_name: el.dataset.gtmListName || '',
    index: 1,
  });

  trackAddToCart(
    {
      item_id: pid || variantPack?.id,
      item_name: el.dataset.productTitle || variantPack?.title || '',
      item_category: el.dataset.productType || '',
      price,
      quantity: 1,
      index: listCtx.index ?? 1,
      box_type: boxType,
      box_size: boxSize,
      box_capacity: boxCapacity,
      purchase_type: purchaseType,
      delivery_frequency: deliveryFrequency,
      item_list_id: listCtx.item_list_id,
      item_list_name: listCtx.item_list_name,
    },
    cartSubtotal
  );

  // Persist tracking data as cart attributes for checkout Custom Pixel.
  if (pid) {
    setGtmCartAttributes(pid, {
      box_type: boxType,
      box_size: boxSize,
      box_capacity: boxCapacity,
      item_list_id: listCtx.item_list_id,
      item_list_name: listCtx.item_list_name,
      index: listCtx.index,
    });
  }
}

// ---------------------------------------------------------------------------
// remove_from_cart
// CartUpdateEvent is dispatched BEFORE morphSection/syncDataCart run, so
// data-cart still holds the OLD cart at listener time. We diff old vs new
// (event.detail.resource.items) to find removed/reduced items.
// This catches both trash-button clicks and minus-to-zero via quantity selector.
// ---------------------------------------------------------------------------

document.addEventListener(ThemeEvents.cartUpdate, (e) => {
  const data = /** @type {CustomEvent} */ (e).detail?.data ?? {};
  if (data.source !== 'cart-items-component' && data.source !== 'cart-items-bundle-remove') return;

  const newItems = /** @type {CustomEvent} */ (e).detail?.resource?.items;
  if (!Array.isArray(newItems)) return;

  const cartComponent = document.querySelector('cart-items-component');
  if (!(cartComponent instanceof HTMLElement)) return;

  let oldCart;
  try {
    oldCart = JSON.parse(cartComponent.dataset.cart ?? '{}');
  } catch (_) {
    return;
  }

  const oldItems = oldCart.items ?? [];

  for (const oldItem of oldItems) {
    const newItem = newItems.find((i) => i.key === oldItem.key);
    const oldQty = oldItem.quantity;
    const newQty = newItem?.quantity ?? 0;
    if (newQty >= oldQty) continue;

    // Bundle trash removes the whole group via cart-items-bundle-remove — roll up once.
    if (
      data.source === 'cart-items-bundle-remove' &&
      oldItem.properties?.['_rc_bundle'] &&
      oldItems.findIndex((i) => i.properties?.['_rc_bundle'] === oldItem.properties?.['_rc_bundle']) !==
        oldItems.indexOf(oldItem)
    ) {
      continue;
    }

    const rolled =
      data.source === 'cart-items-bundle-remove' && oldItem.properties?.['_rc_bundle']
        ? buildRolledUpBundleItem(
            oldItems.filter((i) => i.properties?.['_rc_bundle'] === oldItem.properties?.['_rc_bundle'])
          )
        : null;

    if (rolled) {
      trackRemoveFromCart(rolled, rolled.price);
      continue;
    }

    const removedQty = oldQty - newQty;
    const rawBoxSize = oldItem.properties?.['_box_size'] ?? null;
    const rawBoxCap = oldItem.properties?.['_box_capacity'] ?? null;
    trackRemoveFromCart(
      {
        item_id: resolveGtmItemId(oldItem),
        item_name: cleanCartItemName(oldItem),
        item_category: oldItem.product_type ?? '',
        price: oldItem.price,
        quantity: removedQty,
        index:
          oldItem.properties?.['_index'] != null ? Number(oldItem.properties['_index']) : undefined,
        properties: oldItem.properties,
        box_type: oldItem.properties?.['_box_type'] ?? null,
        box_size: rawBoxSize != null ? Number(rawBoxSize) : null,
        box_capacity: rawBoxCap != null ? Number(rawBoxCap) : null,
        purchase_type: oldItem.properties?.['_purchase_type'] ?? 'one_time',
        delivery_frequency: oldItem.properties?.['_delivery_frequency'] ?? '',
        item_list_id: oldItem.properties?.['_item_list_id'] ?? '',
        item_list_name: oldItem.properties?.['_item_list_name'] ?? '',
      },
      Math.round((oldItem.final_line_price ?? oldItem.line_price ?? 0) * (removedQty / oldQty))
    );
  }
});

// ---------------------------------------------------------------------------
// view_cart
// Fires when cart page loads or cart drawer opens.
// Prefers live /cart.js so capped adds (didError) aren't read from stale data-cart.
// ---------------------------------------------------------------------------

/**
 * Prefer storefront product title (no pack-size / variant suffix).
 * Falls back to `_sb_bundle_title`, then strips Shopify's " - {variant}" suffix.
 *
 * @param {{ title?: string, product_title?: string, properties?: Record<string, string> | null }} item
 * @returns {string}
 */
function cleanCartItemName(item) {
  const properties = item.properties;
  if (properties?.['_sb_bundle_title']) {
    // Cart UI strips " | SKU: …" from `_sb_bundle_title` — keep GTM in sync
    return String(properties['_sb_bundle_title']).split(' | SKU:')[0].trim();
  }
  if (item.product_title) return item.product_title;

  const title = item.title ?? '';
  const sep = title.lastIndexOf(' - ');
  if (sep === -1) return title;

  // Strip pack-size / variant / Recharge SKU suffixes so item_name matches PDP view_item.
  return title.slice(0, sep);
}

/**
 * Parent / shell product id for rolled-up bundle events (matches view_item / add_to_cart).
 * @param {{ product_id?: string | number, variant_id?: string | number, properties?: Record<string, string> | null }} item
 * @returns {string | number}
 */
function resolveGtmItemId(item) {
  const props = item.properties;
  if (props?.['_gtm_item_id']) return normalizeShopifyResourceId(props['_gtm_item_id']);

  const rc = props?.['_rc_bundle'];
  if (rc && String(rc).includes(':')) {
    const parentId = normalizeShopifyResourceId(String(rc).split(':').pop());
    if (parentId) return parentId;
  }

  return normalizeShopifyResourceId(item.product_id ?? item.variant_id) || '';
}

/**
 * @param {any[]} groupLines
 * @returns {object}
 */
function buildRolledUpBundleItem(groupLines) {
  const first = groupLines[0];
  const props = first?.properties ?? {};
  const totalCents = groupLines.reduce(
    (sum, line) => sum + (Number(line.final_line_price ?? line.line_price) || 0),
    0
  );
  const rawBoxSize = props['_box_size'] ?? null;
  const rawBoxCap = props['_box_capacity'] ?? null;

  return {
    item_id: resolveGtmItemId(first),
    item_name: cleanCartItemName(first),
    // Match rolled-up add_to_cart / view_item (component product_type is not the box)
    item_category: '',
    // Rolled-up box: one line at the combined total (matches BYB / custom-bundle ATC)
    price: totalCents,
    quantity: 1,
    index: props['_index'] != null ? Number(props['_index']) : 1,
    properties: props,
    box_type: props['_box_type'] ?? null,
    box_size: rawBoxSize != null ? Number(rawBoxSize) : null,
    box_capacity: rawBoxCap != null ? Number(rawBoxCap) : null,
    purchase_type: props['_purchase_type'] ?? 'one_time',
    delivery_frequency: props['_delivery_frequency'] ?? '',
    item_list_id: props['_item_list_id'] ?? '',
    item_list_name: props['_item_list_name'] ?? '',
  };
}

/**
 * Collapse Recharge/component bundle lines into one ecommerce item (view_item / ATC shape).
 * @param {any[]} cartItems
 * @returns {object[]}
 */
function mapCartItemsForGtm(cartItems) {
  /** @type {object[]} */
  const items = [];
  const seenRcBundles = new Set();

  for (const item of cartItems) {
    const rcBundle = item.properties?.['_rc_bundle'];
    if (rcBundle) {
      if (seenRcBundles.has(rcBundle)) continue;
      seenRcBundles.add(rcBundle);
      items.push(
        buildRolledUpBundleItem(cartItems.filter((line) => line.properties?.['_rc_bundle'] === rcBundle))
      );
      continue;
    }

    const rawBoxSize = item.properties?.['_box_size'] ?? null;
    const rawBoxCap = item.properties?.['_box_capacity'] ?? null;
    items.push({
      item_id: resolveGtmItemId(item),
      item_name: cleanCartItemName(item),
      item_category: item.product_type ?? '',
      price: item.price,
      quantity: item.quantity,
      index: item.properties?.['_index'] != null ? Number(item.properties['_index']) : undefined,
      properties: item.properties,
      box_type: item.properties?.['_box_type'] ?? null,
      box_size: rawBoxSize != null ? Number(rawBoxSize) : null,
      box_capacity: rawBoxCap != null ? Number(rawBoxCap) : null,
      purchase_type: item.properties?.['_purchase_type'] ?? 'one_time',
      delivery_frequency: item.properties?.['_delivery_frequency'] ?? '',
      item_list_id: item.properties?.['_item_list_id'] ?? '',
      item_list_name: item.properties?.['_item_list_name'] ?? '',
    });
  }

  return items;
}

async function fireViewCart() {
  /** @type {any} */
  let cart = null;

  try {
    const res = await fetch('/cart.js');
    if (res.ok) {
      const live = await res.json();
      if (Array.isArray(live?.items)) cart = live;
    }
  } catch (_) {}

  // Fall back to SSR data-cart when /cart.js fails or returns a non-cart payload
  if (!cart?.items) {
    const cartComponent = document.querySelector('cart-items-component');
    if (!(cartComponent instanceof HTMLElement)) return;
    try {
      cart = JSON.parse(cartComponent.dataset.cart ?? '{}');
    } catch (_) {
      return;
    }
  }

  if (!cart?.items?.length) return;

  const items = mapCartItemsForGtm(/** @type {any[]} */ (cart.items));
  const subtotal = cart.items_subtotal_price ?? cart.original_total_price ?? cart.total_price ?? 0;
  trackViewCart(items, cart.total_price ?? 0, subtotal);
}

// Cart page: fire on load if cart-items-component is present and not inside a drawer
if (document.querySelector('cart-items-component:not(cart-drawer-component *)')) {
  fireViewCart();
}

// Cart drawer: fire when the drawer dialog opens.
// cart-drawer-component extends DialogComponent and dispatches dialog:open on itself (no bubbles).
const cartDrawerEl = document.querySelector('cart-drawer-component');
if (cartDrawerEl) {
  cartDrawerEl.addEventListener('dialog:open', fireViewCart);
}
